import type {
  PublicationInboxDetail,
} from '@/hooks/usePublicationInboxDetail';
import type {
  PublicationInboxItem,
} from '@/hooks/usePublicationInbox';

import {
  decodePublicationInboxText,
  formatPublicationInboxDate,
  publicationInboxAttentionClasses,
  publicationInboxAttentionLabel,
  publicationInboxDeadlineText,
  publicationInboxStateLabel,
} from '@/lib/publicationInboxPresentation';

type Props = {
  item: PublicationInboxItem | null;
  detail: PublicationInboxDetail | null;
  isLoading: boolean;
  error: string | null;
};

const valueOrDash = (
  value: string | null | undefined
) => value || '-';

export default function PublicationDetailPanel({
  item,
  detail,
  isLoading,
  error,
}: Props) {
  if (!item) {
    return (
      <div className="rounded-2xl border border-dashed border-background-200 bg-background-50 p-8 text-center text-sm text-foreground-500">
        Selecione uma publicação para abrir o detalhe.
      </div>
    );
  }

  if (isLoading && !detail) {
    return (
      <div className="h-96 animate-pulse rounded-2xl border border-background-200 bg-background-100" />
    );
  }

  if (error) {
    return (
      <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  const resolved = detail?.item ?? item;
  const tone =
    publicationInboxAttentionClasses(
      resolved.workflow.attention
    );
  const analysis = detail?.analysis ?? null;

  return (
    <article className="space-y-4 rounded-2xl border border-background-200 bg-background-50 p-4">
      <header className="space-y-3 border-b border-background-200 pb-4">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`rounded-full border px-2.5 py-1 text-[11px] font-semibold ${tone.badge}`}
          >
            {publicationInboxAttentionLabel(
              resolved.workflow.attention
            )}
          </span>
          <span className="rounded-full border border-background-200 bg-white px-2.5 py-1 text-[11px] font-medium text-foreground-600">
            {publicationInboxStateLabel(
              resolved.workflow.state
            )}
          </span>
        </div>

        <div>
          <h3 className="text-lg font-bold leading-6 text-foreground-900">
            {decodePublicationInboxText(resolved.publication.title)}
          </h3>
          <p className="mt-1 text-sm text-foreground-600">
            {resolved.context.clientName ??
              'Cliente não identificado'}
          </p>
        </div>

        <dl className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
          <div>
            <dt className="text-foreground-400">
              Processo
            </dt>
            <dd className="mt-0.5 font-medium text-foreground-700">
              {valueOrDash(
                resolved.publication.processNumber
              )}
            </dd>
          </div>
          <div>
            <dt className="text-foreground-400">
              Caso
            </dt>
            <dd className="mt-0.5 font-medium text-foreground-700">
              {valueOrDash(
                resolved.context.caseTitle
              )}
            </dd>
          </div>
          <div>
            <dt className="text-foreground-400">
              Tribunal / fonte
            </dt>
            <dd className="mt-0.5 font-medium text-foreground-700">
              {[
                resolved.publication.tribunal,
                resolved.publication.sourceName ??
                  resolved.publication.source,
              ]
                .filter(Boolean)
                .join(' • ') || '-'}
            </dd>
          </div>
          <div>
            <dt className="text-foreground-400">
              Captura
            </dt>
            <dd className="mt-0.5 font-medium text-foreground-700">
              {formatPublicationInboxDate(
                resolved.publication.capturedAt
              )}
            </dd>
          </div>
        </dl>
      </header>

      <section className="rounded-xl border border-background-200 bg-white p-3">
        <div className="flex items-center justify-between gap-2">
          <h4 className="text-sm font-semibold text-foreground-800">
            Análise jurídica assistida
          </h4>
          {resolved.ai.confidence && (
            <span className="text-[10px] uppercase tracking-wide text-foreground-400">
              confiança {resolved.ai.confidence}
            </span>
          )}
        </div>

        {resolved.flags.aiFailed ? (
          <div className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
            A análise automática apresentou falha. A publicação permanece ativa para decisão humana.
          </div>
        ) : analysis || resolved.ai.summary ? (
          <div className="mt-3 space-y-3 text-sm">
            <p className="leading-6 text-foreground-700">
              {analysis?.summary?.concise ??
                analysis?.summary?.title ??
                resolved.ai.summary}
            </p>

            <div className="grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
              <div>
                <span className="text-foreground-400">
                  Exige ação
                </span>
                <p className="mt-0.5 font-medium text-foreground-700">
                  {valueOrDash(
                    analysis?.action?.requiresAction ??
                      resolved.ai.requiresAction
                  )}
                </p>
              </div>
              <div>
                <span className="text-foreground-400">
                  Tipo
                </span>
                <p className="mt-0.5 font-medium text-foreground-700">
                  {valueOrDash(
                    analysis?.action?.actionType ??
                      resolved.ai.actionType
                  )}
                </p>
              </div>
            </div>

            {(analysis?.action?.suggestedAction ??
              resolved.ai.suggestedAction) && (
              <div className="rounded-lg bg-background-50 p-3 text-xs text-foreground-700">
                <span className="font-semibold">
                  Providência sugerida:
                </span>{' '}
                {analysis?.action
                  ?.suggestedAction ??
                  resolved.ai.suggestedAction}
              </div>
            )}
          </div>
        ) : (
          <p className="mt-3 text-xs text-foreground-500">
            Ainda não existe análise estruturada disponível para esta publicação.
          </p>
        )}
      </section>

      {resolved.flags.possibleDeadline && (
        <section className="rounded-xl border border-red-200 bg-red-50/60 p-3">
          <div className="flex items-center gap-2">
            <i className="ri-timer-flash-line text-red-600"></i>
            <h4 className="text-sm font-semibold text-red-800">
              Possível prazo
            </h4>
          </div>

          <div className="mt-3 grid grid-cols-1 gap-2 text-xs sm:grid-cols-2">
            <div>
              <span className="text-red-500">
                Sugestão
              </span>
              <p className="mt-0.5 font-semibold text-red-800">
                {publicationInboxDeadlineText(
                  analysis?.deadline?.value ??
                    resolved.ai.deadlineValue,
                  analysis?.deadline?.unit ??
                    resolved.ai.deadlineUnit
                ) ?? 'Prazo identificado'}
              </p>
            </div>
            <div>
              <span className="text-red-500">
                Data final
              </span>
              <p className="mt-0.5 font-semibold text-red-800">
                {resolved.operation
                  .deadlineDueDate
                  ? formatPublicationInboxDate(
                      resolved.operation
                        .deadlineDueDate,
                      false
                    )
                  : 'Não confirmada'}
              </p>
            </div>
          </div>

          {!resolved.operation
            .deadlineDueDate && (
            <p className="mt-3 text-xs leading-5 text-red-700">
              A IA não confirma a data final. A validação jurídica permanece obrigatória.
            </p>
          )}
        </section>
      )}

      {resolved.flags.hearing && (
        <section className="rounded-xl border border-amber-200 bg-amber-50 p-3">
          <div className="flex items-center gap-2 text-amber-800">
            <i className="ri-calendar-event-line"></i>
            <h4 className="text-sm font-semibold">
              Audiência identificada
            </h4>
          </div>
          <p className="mt-2 text-xs leading-5 text-amber-700">
            A classificação indica audiência ou comparecimento. Confira o texto original antes de qualquer providência.
          </p>
        </section>
      )}

      {detail?.exceptions &&
        detail.exceptions.length > 0 && (
          <section className="rounded-xl border border-amber-200 bg-amber-50 p-3">
            <h4 className="text-sm font-semibold text-amber-800">
              Pendências / exceções
            </h4>
            <div className="mt-2 space-y-2">
              {detail.exceptions.map(
                (exception) => (
                  <div
                    key={exception.id}
                    className="rounded-lg border border-amber-200 bg-white/70 p-2 text-xs text-amber-800"
                  >
                    <p className="font-semibold">
                      {exception.title}
                    </p>
                    {exception.detail && (
                      <p className="mt-1 leading-5">
                        {exception.detail}
                      </p>
                    )}
                  </div>
                )
              )}
            </div>
          </section>
        )}

      <section className="rounded-xl border border-background-200 bg-white p-3">
        <h4 className="text-sm font-semibold text-foreground-800">
          Publicação original
        </h4>

        {detail ? (
          <>
            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-foreground-400">
              <span>
                Disponibilizada:{' '}
                {formatPublicationInboxDate(
                  detail.publication.availableAt
                )}
              </span>
              <span>
                Publicada:{' '}
                {formatPublicationInboxDate(
                  detail.publication.publishedAt
                )}
              </span>
              {detail.publication.partyNames && (
                <span>
                  Partes:{' '}
                  {decodePublicationInboxText(
                    detail.publication.partyNames
                  )}
                </span>
              )}
            </div>

            <div className="mt-3 max-h-72 overflow-y-auto whitespace-pre-wrap rounded-lg bg-background-50 p-3 text-xs leading-5 text-foreground-700">
              {decodePublicationInboxText(
                detail.publication.content
              ) ||
                'Texto original indisponível.'}
            </div>
          </>
        ) : (
          <p className="mt-2 text-xs text-foreground-500">
            Carregando texto integral...
          </p>
        )}
      </section>

      {detail?.history &&
        detail.history.length > 0 && (
          <section className="rounded-xl border border-background-200 bg-white p-3">
            <h4 className="text-sm font-semibold text-foreground-800">
              Histórico
            </h4>
            <ol className="mt-3 space-y-3 border-l border-background-200 pl-4">
              {detail.history.map(
                (historyItem, index) => (
                  <li
                    key={`${historyItem.kind}-${historyItem.at}-${index}`}
                    className="relative"
                  >
                    <span className="absolute -left-[19px] top-1 h-2 w-2 rounded-full bg-foreground-300"></span>
                    <p className="text-xs font-medium text-foreground-700">
                      {historyItem.title}
                    </p>
                    {historyItem.detail && (
                      <p className="mt-0.5 text-[11px] leading-4 text-foreground-500">
                        {historyItem.detail}
                      </p>
                    )}
                    <p className="mt-0.5 text-[10px] text-foreground-400">
                      {formatPublicationInboxDate(
                        historyItem.at
                      )}
                    </p>
                  </li>
                )
              )}
            </ol>
          </section>
        )}
    </article>
  );
}